import { useState, useEffect } from 'react';

interface Breakpoints {
  /** >= 640px */
  sm: boolean;
  /** >= 768px */
  md: boolean;
  /** >= 1024px */
  lg: boolean;
  /** >= 1280px */
  xl: boolean;
}

const QUERIES: Record<keyof Breakpoints, string> = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
};

function getMatches(): Breakpoints {
  if (typeof window === 'undefined') {
    return { sm: false, md: false, lg: false, xl: false };
  }
  return {
    sm: window.matchMedia(QUERIES.sm).matches,
    md: window.matchMedia(QUERIES.md).matches,
    lg: window.matchMedia(QUERIES.lg).matches,
    xl: window.matchMedia(QUERIES.xl).matches,
  };
}

/**
 * Responsive breakpoint detection hook.
 *
 * Uses `window.matchMedia` to track Tailwind CSS breakpoints (sm/md/lg/xl).
 * Returns an object with boolean flags for each breakpoint.
 */
export function useResponsive(): Breakpoints {
  const [breakpoints, setBreakpoints] = useState<Breakpoints>(getMatches);

  useEffect(() => {
    const mediaQueryLists = Object.entries(QUERIES).map(([key, query]) => ({
      key: key as keyof Breakpoints,
      mql: window.matchMedia(query),
    }));

    function handleChange() {
      setBreakpoints(getMatches());
    }

    mediaQueryLists.forEach(({ mql }) => {
      mql.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueryLists.forEach(({ mql }) => {
        mql.removeEventListener('change', handleChange);
      });
    };
  }, []);

  return breakpoints;
}
